import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getOperations } from '../../api/operations.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

// Map the backend CustomerType `color` token to badge + dot classes. Any
// unrecognised token falls back to a neutral slate badge.
const COLOR_STYLE = {
  emerald: { dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  sky: { dot: 'bg-sky-500', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  violet: { dot: 'bg-violet-500', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
  amber: { dot: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  rose: { dot: 'bg-rose-500', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  teal: { dot: 'bg-teal-500', badge: 'bg-teal-50 text-teal-700 border-teal-200' },
  indigo: { dot: 'bg-indigo-500', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  cyan: { dot: 'bg-cyan-500', badge: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  slate: { dot: 'bg-slate-400', badge: 'bg-slate-50 text-slate-600 border-slate-200' },
}
const FALLBACK = COLOR_STYLE.slate

function styleForColor(color) {
  return COLOR_STYLE[color] || FALLBACK
}

function formatWhen(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return ''
  }
}

function StatTile({ label, value, accent = 'text-slate-900' }) {
  return (
    <div className="rounded-xl border border-sand-200/60 bg-white px-3 py-2.5">
      <div className={`text-2xl font-display font-semibold leading-none ${accent}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{label}</div>
    </div>
  )
}

function CustomerBadge({ label, color }) {
  const st = styleForColor(color)
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] ${st.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {label}
    </span>
  )
}

function SourceBadge({ source }) {
  const self = source === 'self'
  return (
    <span
      className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${
        self
          ? 'bg-wantok-50 text-wantok-700 border-wantok-200'
          : 'bg-slate-100 text-slate-600 border-slate-200'
      }`}
    >
      {self ? 'Self' : 'Staff'}
    </span>
  )
}

export default function OperationsDashboard() {
  const [days, setDays] = useState(7)
  const [customerType, setCustomerType] = useState('')

  const q = useQuery({
    queryKey: ['operations-dashboard', days, customerType],
    queryFn: () => getOperations({ days, customerType }),
    placeholderData: (prev) => prev, // keep old rows visible while refetching
  })

  const data = q.data
  const types = data?.customer_types || []
  const np = data?.sections?.new_patients
  const rows = np?.rows || []
  const orgActivity = data?.sections?.org_activity || []

  return (
    <div className="space-y-4">
      {/* ---- Header + window/customer-type filters ---- */}
      <Card>
        <CardHeader
          title="Operations dashboard"
          subtitle="Who joined, where they came from, and how — the platform's daily operational view."
          right={
            <div className="flex items-center gap-2">
              <select
                value={customerType}
                onChange={(e) => setCustomerType(e.target.value)}
                className="border rounded px-2 py-1 text-xs bg-white"
                aria-label="Filter by customer type"
              >
                <option value="">All customer types</option>
                {types.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="border rounded px-2 py-1 text-xs bg-white"
                aria-label="Time window"
              >
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
          }
        />

        {np && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <StatTile label="New today" value={np.new_today} accent="text-wantok-700" />
            <StatTile label={`In window (${data.window_days}d)`} value={np.new_in_window} />
            <StatTile label="Self-registered" value={np.by_source?.self ?? 0} />
            <StatTile label="Staff-registered" value={np.by_source?.staff ?? 0} />
            {(np.by_customer_type || [])
              .filter((c) => c.count > 0)
              .slice(0, 2)
              .map((c) => (
                <StatTile key={c.code} label={c.label} value={c.count} />
              ))}
          </div>
        )}
      </Card>

      {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}

      {/* ---- Section: New patients ---- */}
      <Card>
        <CardHeader
          title="New patients"
          subtitle={
            q.isLoading
              ? 'Loading…'
              : `${rows.length} shown${np?.truncated ? ' (capped at 500 — narrow the window)' : ''}`
          }
        />

        {!q.isLoading && rows.length === 0 && (
          <div className="text-sm text-slate-400 py-6 text-center">
            No registrations in this window.
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Customer type</th>
                  <th className="py-2 pr-3 font-medium">Source</th>
                  <th className="py-2 pr-3 font-medium">Origin</th>
                  <th className="py-2 pr-3 font-medium">Contact</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-500 text-xs">
                      {formatWhen(r.created_at)}
                    </td>
                    <td className="py-2 pr-3 font-medium text-slate-800">{r.name}</td>
                    <td className="py-2 pr-3">
                      <CustomerBadge label={r.customer_type_label} color={r.customer_type_color} />
                    </td>
                    <td className="py-2 pr-3">
                      <SourceBadge source={r.source} />
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{r.source_detail}</td>
                    <td className="py-2 pr-3 text-slate-500 text-xs">
                      {[r.phone, r.email].filter(Boolean).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---- Section: Organisation activity ---- */}
      <Card>
        <CardHeader
          title="Organisation activity"
          subtitle="Where this window's sign-ups came from."
        />
        {orgActivity.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No activity in this window.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {orgActivity.map((o) => (
              <div
                key={o.organization}
                className="flex items-center justify-between rounded-xl border border-sand-200/60 bg-white px-3 py-2"
              >
                <span className="text-sm text-slate-700 truncate">{o.organization}</span>
                <span className="text-sm font-display font-semibold text-slate-900 ml-2">
                  {o.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
